import React, { Suspense } from 'react'
import styled from 'styled-components'
import { Column } from 'components/common/layout'
import { MediumText, SemiBoldText } from 'components/common/text'
import { theme } from 'infra/theme'
import Image from 'next/legacy/image'
import { ProgressBar } from 'components/common/progress-bar'
import { FloatingButton } from 'components/scouts/common/styles'
import { ChipSize, ChipWrapper, DeletableChip } from 'components/common/chip'
import { ScoutCalculatorStore } from 'stores/scouts/scout-calculator'
import { observer } from 'mobx-react'
import { ExperienceSlider } from 'components/scouts/greeting/experience-slider'
import { displayExperienceDesc } from 'utils/displays'
import { PositionSelector } from 'components/misc/selector/position-selector'
import { SelectorCard } from 'components/misc/selector/selector-wrapper'
import { TechStackSelector } from 'components/misc/selector/tech-stack-selector'
import { PRE_USER_INFO_KEY } from 'infra/constants'
import { analytics } from 'infra/analytics'

const ExperienceProgress: React.FC<{ store: ScoutCalculatorStore }> = observer(
  ({ store }) => {
    return (
      <ExperienceWrapper>
        <SemiBoldText
          style={{ fontSize: theme.fontSize.h5, textAlign: 'center' }}
        >
          {displayExperienceDesc(store.experience)}
          {store.experience === 0 ? ' (인턴/신입 포함)' : ''}
        </SemiBoldText>
        <ExperienceSlider
          value={store.experience}
          onChange={(experience) => store.setExperience(experience)}
        />
      </ExperienceWrapper>
    )
  },
)

const PositionProgress: React.FC<{ store: ScoutCalculatorStore }> = observer(
  ({ store }) => {
    return (
      <>
        <PositionTechStackSelectorCard>
          <PositionSelector
            selectedPositionTypes={store.positionTypes}
            onChange={(p) => store.setPositionTypes(p)}
          />
        </PositionTechStackSelectorCard>
        {store.positionTypes.length > 0 && (
          <PositionTechStackChipContainer>
            {store.positionTypes.map((p) => (
              <DeletableChip
                key={p.id}
                size={ChipSize.SMALL}
                isSelected
                onClick={() => store.togglePositionType(p)}
              >
                {p.name}
              </DeletableChip>
            ))}
          </PositionTechStackChipContainer>
        )}
      </>
    )
  },
)

const TechStackProgress: React.FC<{ store: ScoutCalculatorStore }> = observer(
  ({ store }) => {
    return (
      <>
        <PositionTechStackSelectorCard>
          <TechStackSelector
            selectedPositionTypes={store.positionTypeIds}
            selected={store.techStacks.map((t) => t.id)}
            onClick={(item) => store.setTechStack(item)}
          />
        </PositionTechStackSelectorCard>
        {store.techStacks.length > 0 && (
          <PositionTechStackChipContainer>
            {store.techStacks.map((item) => (
              <DeletableChip
                key={item.id}
                isSelected
                size={ChipSize.SMALL}
                onClick={() => store.deleteTechStack(item)}
              >
                {item.name}
              </DeletableChip>
            ))}
          </PositionTechStackChipContainer>
        )}
      </>
    )
  },
)

export const CalculatingProgress: React.FC<{ store: ScoutCalculatorStore }> =
  observer(({ store }) => {
    return (
      <Column
        style={{ gap: 12, height: '100%', justifyContent: 'space-between' }}
      >
        <Column style={{ gap: 12 }}>
          <ImageWrapper>
            <Image
              src='/steve.svg'
              layout='fill'
              objectFit='cover'
              alt='인간지능'
            />
          </ImageWrapper>
          <SemiBoldText
            style={{ fontSize: theme.fontSize.h4, lineHeight: 1.4 }}
          >
            {store.step.question}
          </SemiBoldText>
          <MediumText style={{ color: theme.color.gray2 }}>
            3가지 기본 정보를 입력하고 바로 확인해보세요
          </MediumText>
          <ProgressBar total={3} progress={store.step.progress} />
          {store.step.progress === 1 && <ExperienceProgress store={store} />}
          {store.step.progress === 2 && (
            <Suspense fallback={<div style={{ height: 256 + 48 }} />}>
              <PositionProgress store={store} />
            </Suspense>
          )}
          {store.step.progress === 3 && (
            <Suspense fallback={<div style={{ height: 256 + 48 }} />}>
              <TechStackProgress store={store} />
            </Suspense>
          )}
        </Column>
        <StickyButton
          disabled={!store.isAbleToNext}
          onClick={() => {
            analytics.track(
              store.isLastStep
                ? 'click_sp_greeting_calculting_done_btn'
                : 'click_sp_greeting_calculting_next_btn',
            )
            store.isLastStep
              ? store.setIsCalculated(true)
              : store.setStepIndex(store.stepIndex + 1)
            localStorage.setItem(
              PRE_USER_INFO_KEY,
              JSON.stringify({
                experience: store.experience,
                interestedPositionTypes: store.positionTypes,
                interestedTechStacks: store.techStacks,
              }),
            )
          }}
        >
          {store.isLastStep ? '👉🏻 내가 받을 제안 확인하기' : '다음'}
        </StickyButton>
      </Column>
    )
  })

const ExperienceWrapper = styled(Column)`
  gap: 24px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    margin: 48px 16px 56px;
  }
  @media ${(p) => p.theme.deviceSize.desktop} {
    width: 360px;
    margin: 56px auto;
  }
`

const ImageWrapper = styled.div`
  position: relative;
  width: 53px;
  height: 53px;
  overflow: hidden;
`

const PositionTechStackSelectorCard = styled(SelectorCard)`
  margin: 24px 0;
`

const PositionTechStackChipContainer = styled(ChipWrapper)`
  margin: -24px 0 16px;
`

const StickyButton = styled(FloatingButton)`
  border-radius: 16px;
  position: sticky;
  bottom: 24px;
  @media ${(p) => p.theme.deviceSize.desktop} {
    bottom: 0px;
  }
`
